// src/components/package/ManagePackageLayer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaEdit,
  FaTrash,
  FaSearch,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaBoxOpen,
} from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getPackages,
  createPackage,
  updatePackage,
  deletePackage,
} from "../../services/manageSchoolPackageService";

import "./StudentListLayer.css";

const initialForm = {
  package_name: "",
  student_limit: "",
  price: "",
  validity_days: "",
  has_sms_feature: 1, // Default to Enabled (Active)
};

const ManagePackageLayer = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [smsFilter, setSmsFilter] = useState("all");

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [formData, setFormData] = useState(initialForm);
  const [selectedPackage, setSelectedPackage] = useState(null);

  /* ================= FETCH ================= */

  const fetchPackages = async () => {
    try {
      setLoading(true);

      const res = await getPackages();

      if (res?.data?.status) {
        setPackages(res.data.data || []);
      } else if (res?.status) {
        setPackages(res.data || []);
      } else {
        setPackages([]);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  /* ================= CREATE ================= */

  const handleCreate = async () => {
    try {
      if (!formData.package_name) {
        return toast.warning("Package name required");
      }

      await createPackage({
        package_name: formData.package_name,
        student_limit: Number(formData.student_limit),
        price: Number(formData.price),
        validity_days: Number(formData.validity_days),
        has_sms_feature: Number(formData.has_sms_feature),
      });

      toast.success("Package created successfully");

      setShowAddModal(false);
      setFormData(initialForm);

      fetchPackages();
    } catch (error) {
      console.log(error);
      toast.error("Failed to create package");
    }
  };

  /* ================= UPDATE ================= */

  const openEditModal = (item) => {
    setSelectedPackage({ ...item });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      await updatePackage({
        id: selectedPackage.id,
        package_name: selectedPackage.package_name,
        student_limit: Number(selectedPackage.student_limit),
        price: Number(selectedPackage.price),
        validity_days: Number(selectedPackage.validity_days),
        has_sms_feature: Number(selectedPackage.has_sms_feature),
      });

      toast.success("Package updated successfully");

      setShowEditModal(false);

      fetchPackages();
    } catch (error) {
      console.log(error);
      toast.error("Failed to update package");
    }
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this package ?")) return;

    try {
      await deletePackage(id);

      toast.success("Package deleted");

      fetchPackages();
    } catch (error) {
      console.log(error);
      toast.error("Delete failed");
    }
  };

  /* ================= FILTER ================= */

  const filteredPackages = useMemo(() => {
    return packages.filter((item) => {
      const searchMatch = item.package_name
        ?.toLowerCase()
        .includes(search.toLowerCase());

      const smsMatch =
        smsFilter === "all"
          ? true
          : smsFilter === "enabled"
          ? Number(item.has_sms_feature) === 1
          : Number(item.has_sms_feature) === 0;

      return searchMatch && smsMatch;
    });
  }, [packages, search, smsFilter]);

  /* ================= PAGINATION ================= */

  const totalPages = Math.ceil(
    filteredPackages.length / pageSize
  );

  const displayed = filteredPackages.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  return (
    <div className="student-wrapper">
      <div className="student-card">

        {/* HEADER */}

        <div className="list-header">
          <div>
            <h5>Package Management</h5>

            <p className="text-secondary-light">
              Manage Subscription Packages
            </p>
          </div>

          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <FaPlus className="me-2" />
              Add Package
            </button>
          </div>
        </div>

        {/* FILTERS */}

        <div className="list-controls">

          <div className="filter-group">

            <div className="search-box">
              <FaSearch />
              <input
                type="text"
                placeholder="Search package..."
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
              />
            </div>

            <div className="class-filter">
              <select
                value={smsFilter}
                onChange={(e) =>
                  setSmsFilter(e.target.value)
                }
              >
                <option value="all">
                  All Packages
                </option>

                <option value="enabled">
                  SMS Enabled
                </option>

                <option value="disabled">
                  SMS Disabled
                </option>
              </select>
            </div>

          </div>

        </div>

        {/* TABLE */}

        <div className="table-responsive">
          <table className="user-table">

            <thead>
              <tr>
                <th>ID</th>
                <th>Package</th>
                <th>Students</th>
                <th>Price</th>
                <th>Validity</th>
                <th>SMS</th>
                <th>Created</th>
                <th className="text-center">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>

              {loading ? (
                <tr>
                  <td
                    colSpan="8"
                    className="no-data"
                  >
                    Loading Packages...
                  </td>
                </tr>
              ) : displayed.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="no-data"
                  >
                    No Packages Found
                  </td>
                </tr>
              ) : (
                displayed.map((item) => (
                  <tr key={item.id}>

                    <td>{item.id}</td>

                    <td>
                      <div className="d-flex align-items-center gap-3">

                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: "#EEF4FF",
                            color: "#2563EB",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaBoxOpen />
                        </div>

                        <div>
                          <strong>
                            {item.package_name}
                          </strong>
                        </div>

                      </div>
                    </td>

                    <td>{item.student_limit}</td>

                    <td>
                      ₹{item.price}
                    </td>

                    <td>
                      {item.validity_days} Days
                    </td>

                    <td>
                      <span
                        className={`badge ${
                          Number(
                            item.has_sms_feature
                          ) === 1
                            ? "bg-success"
                            : "bg-danger"
                        }`}
                      >
                        {Number(
                          item.has_sms_feature
                        ) === 1
                          ? "Enabled"
                          : "Disabled"}
                      </span>
                    </td>

                    <td>
                      {item.created_at ||
                        "-"}
                    </td>

                    <td className="actions">

                      <button
                        title="Edit"
                        onClick={() =>
                          openEditModal(item)
                        }
                        style={{
                          color: "#2563EB",
                        }}
                      >
                        <FaEdit size={18} />
                      </button>

                      <button
                        title="Delete"
                        onClick={() =>
                          handleDelete(item.id)
                        }
                        style={{
                          color: "#DC2626",
                        }}
                      >
                        <FaTrash size={18} />
                      </button>

                    </td>

                  </tr>
                ))
              )}

            </tbody>

          </table>
        </div>

        {/* PAGINATION */}

        {totalPages > 1 && (
          <div className="pagination">

            <span className="text-secondary-light">
              Showing {displayed.length} of{" "}
              {filteredPackages.length} Packages
            </span>

            <div className="d-flex gap-2">

              <button
                disabled={page === 1}
                className="btn-sm"
                onClick={() =>
                  setPage(page - 1)
                }
              >
                <FaChevronLeft />
              </button>

              <button
                disabled={page === totalPages}
                className="btn-sm"
                onClick={() =>
                  setPage(page + 1)
                }
              >
                <FaChevronRight />
              </button>

            </div>

          </div>
        )}

      </div>

      {/* ADD MODAL */}

      {showAddModal && (
        <div className="modal fade show d-block">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header">
                <h5>Add Package</h5>

                <button
                  className="btn-close"
                  onClick={() =>
                    setShowAddModal(false)
                  }
                />
              </div>

              <div className="modal-body">

                <div className="row g-3">

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Package Name</label>
                    <input
                      className="form-control"
                      placeholder="Enter Package Name"
                      value={formData.package_name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          package_name:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Student Limit</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter Student Limit"
                      value={formData.student_limit}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          student_limit:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Price (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter Price"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Validity (Days)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter Validity Days"
                      value={formData.validity_days}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          validity_days:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">SMS Status</label>
                    <select
                      className="form-select"
                      value={formData.has_sms_feature}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          has_sms_feature: Number(e.target.value),
                        })
                      }
                    >
                      <option value={1}>Active (Enabled)</option>
                      <option value={0}>Inactive (Disabled)</option>
                    </select>
                  </div>

                </div>

              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowAddModal(false)
                  }
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleCreate}
                >
                  Create Package
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}

      {showEditModal && selectedPackage && (
        <div className="modal fade show d-block">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">

              <div className="modal-header">
                <h5>Update Package</h5>

                <button
                  className="btn-close"
                  onClick={() =>
                    setShowEditModal(false)
                  }
                />
              </div>

              <div className="modal-body">

                <div className="row g-3">

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Package Name</label>
                    <input
                      className="form-control"
                      value={
                        selectedPackage.package_name
                      }
                      onChange={(e) =>
                        setSelectedPackage({
                          ...selectedPackage,
                          package_name:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Student Limit</label>
                    <input
                      type="number"
                      className="form-control"
                      value={
                        selectedPackage.student_limit
                      }
                      onChange={(e) =>
                        setSelectedPackage({
                          ...selectedPackage,
                          student_limit:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Price (₹)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={
                        selectedPackage.price
                      }
                      onChange={(e) =>
                        setSelectedPackage({
                          ...selectedPackage,
                          price:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Validity (Days)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={
                        selectedPackage.validity_days
                      }
                      onChange={(e) =>
                        setSelectedPackage({
                          ...selectedPackage,
                          validity_days:
                            e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">SMS Status</label>
                    <select
                      className="form-select"
                      value={selectedPackage.has_sms_feature}
                      onChange={(e) =>
                        setSelectedPackage({
                          ...selectedPackage,
                          has_sms_feature: Number(e.target.value),
                        })
                      }
                    >
                      <option value={1}>Active (Enabled)</option>
                      <option value={0}>Inactive (Disabled)</option>
                    </select>
                  </div>

                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setShowEditModal(false)
                  }
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={handleUpdate}
                >
                  Update Package
                </button>

              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePackageLayer;